import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import useMemberDashboard from "../hooks/useMemberDashboard";
import useLogin from "../hooks/useLogin";
import { Link } from "react-router-dom";
import copy from "copy-to-clipboard";

const StatCard = ({ title, value, icon, color, onClick }) => (
  <div className={`stat-card ${color}${onClick ? " clickable" : ""}`} onClick={onClick}>
    <div className="stat-icon">{icon}</div>
    <div className="stat-content">
      <h3>{value}</h3>
      <p>{title}</p>
    </div>
  </div>
);

const NotificationModal = ({ notification, onConfirm, onClose }) => (
  <div className="modal-overlay">
    <div className="modal-content notification-modal">
      <div className="modal-header">
        <h2>{notification.title}</h2>
        <span className={`notification-type ${notification.type}`}>
          {notification.type}
        </span>
      </div>
      <div className="modal-body">
        <p>{notification.content}</p>
        <div className="notification-info">
          <span>بواسطة: {notification.createdBy}</span>
          <span>التاريخ: {notification.createdAt.toLocaleString()}</span>
        </div>
      </div>
      <div className="modal-footer">
        <button
          className="primary-button"
          onClick={onConfirm}
          disabled={notification.isRead}
        >
          {notification.isRead ? "تم القراءة" : "تأكيد القراءة"}
        </button>
        <button className="secondary-button" onClick={onClose}>
          إغلاق
        </button>
      </div>
    </div>
  </div>
);

// Pop-up يظهر مرة واحدة فقط بعد التفعيل
const WelcomePopup = ({ onClose }) => (
  <div className="welcome-popup">
    <div className="popup-content">
      <h2>🎉 تم تفعيل حسابك!</h2>
      <p>
        أنت الآن مشترك تلقائيًا في جميع الأنشطة المتاحة.
        <br />
        يمكنك إدارة اشتراكاتك من صفحة{" "}
        <Link to="/profile" style={{ color: "#3483fa", fontWeight: "bold" }}>
          الملف الشخصي
        </Link>
        .
      </p>
      <button className="primary-button" onClick={onClose}>
        إغلاق
      </button>
    </div>
  </div>
);

function MemberDashboardPage() {
  const { currentUser } = useAuth();
  const {
    memberData,
    activities,
    notifications,
    stats,
    loading,
    markNotificationAsRead,
    refreshData,
  } = useMemberDashboard(currentUser);

  const { getRedirectUrlAfterLogin } = useLogin();

  const [selectedNotification, setSelectedNotification] = useState(null);
  const [userReferralLink, setUserReferralLink] = useState("");
  const [copyMsg, setCopyMsg] = useState("");
  const [redirectLoading, setRedirectLoading] = useState(false);
  const [redirectError, setRedirectError] = useState("");
  const [showWelcome, setShowWelcome] = useState(false);

  // Welcome Popup يظهر مرة واحدة فقط بعد التفعيل
  useEffect(() => {
    if (currentUser && currentUser.isActive) {
      const shown = localStorage.getItem("welcome_shown");
      if (!shown) {
        setShowWelcome(true);
        localStorage.setItem("welcome_shown", "1");
      }
    }
  }, [currentUser]);

  useEffect(() => {
    async function fetchUserReferralLink() {
      if (!currentUser?.uid) return;
      // جلب أول referralLink لهذا المستخدم فقط للعرض والنسخ السريع
      try {
        const { collection, query, where, getDocs } = await import(
          "firebase/firestore"
        );
        const { db } = await import("../firebaseConfig");
        const referralLinksRef = collection(db, "referralLinks");
        const q = query(
          referralLinksRef,
          where("memberId", "==", currentUser.uid)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const doc = snapshot.docs[0].data();
          setUserReferralLink(doc.referralLink || "");
        } else {
          setUserReferralLink("");
        }
      } catch (err) {
        setUserReferralLink("");
      }
    }
    fetchUserReferralLink();
  }, [currentUser]);

  const handleRedirectToLanding = async () => {
    setRedirectError("");
    setRedirectLoading(true);
    try {
      if (!currentUser || !currentUser.uid) {
        setRedirectError("لا يوجد بيانات مستخدم!");
        setRedirectLoading(false);
        return;
      }
      const data = await getRedirectUrlAfterLogin(currentUser.uid);
      if (data && data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        setRedirectError("تعذر جلب رابط النسخة الأصلية. يرجى المحاولة لاحقًا.");
      }
    } catch (err) {
      setRedirectError("حدث خطأ أثناء تحويلك للنسخة الأصلية.");
    } finally {
      setRedirectLoading(false);
    }
  };

  if (loading) {
    return <div className="loading-container">جاري تحميل البيانات...</div>;
  }

  const handleReadNotification = async () => {
    if (!selectedNotification || selectedNotification.isRead) return;
    try {
      await markNotificationAsRead(selectedNotification.id);
      await refreshData();
    } catch (error) {
      alert(error.message);
    }
  };

  return (
    <div className="container">
      <div className="dashboard-header">
        <h1>مرحباً، {memberData?.name}</h1>
        <div className="member-info">
          <Link to="/profile" className="view-profile-link">
            عرض الملف الشخصي
          </Link>
        </div>
      </div>

      {/* Welcome Pop-up يظهر مرة واحدة */}
      {showWelcome && <WelcomePopup onClose={() => setShowWelcome(false)} />}

      {currentUser?.projectId && (
        <div className="dashboard-actions">
          <button
            className="primary-button dashboard-action-btn"
            onClick={handleRedirectToLanding}
            disabled={redirectLoading}
          >
            {redirectLoading
              ? "جاري تحويلك إلى متاجر الحي..."
              : "🔄 الرجوع إلى متاجر الحي"}
          </button>
          {userReferralLink && (
            <button
              className="secondary-button dashboard-action-btn copy-referral-btn"
              onClick={() => {
                copy(userReferralLink);
                setCopyMsg("تم نسخ رابط العضوية!");
                setTimeout(() => setCopyMsg(""), 1800);
              }}
            >
              📋 نسخ رابط العضوية
            </button>
          )}
          {copyMsg && (
            <span className="copy-success-msg">
              {copyMsg}
            </span>
          )}
          {redirectError && (
            <div className="error-message" style={{ marginTop: 5 }}>
              {redirectError}
            </div>
          )}
        </div>
      )}

      <div className="stats-grid">
        <StatCard
          title="النقاط"
          value={`${memberData?.points || 0} / 5000`}
          icon="🏆"
          color="primary"
        />
        <StatCard
          title="الأنشطة"
          value={stats.activitiesCount}
          icon="📋"
          color="success"
          onClick={() => document.getElementById("activities-section")?.scrollIntoView({ behavior: "smooth" })}
        />
        <StatCard
          title="المكافآت"
          value={stats.rewardsCount}
          icon="🎁"
          color="warning"
        />
        <StatCard
          title="إشعارات جديدة"
          value={stats.unreadNotifications}
          icon="🔔"
          color="danger"
        />
      </div>

      <div className="dashboard-sections">
        <section id="activities-section" className="dashboard-section activities-section-featured">
          <div className="section-header">
            <h2>الأنشطة المتاحة لعضو متاجر الحي</h2>
          </div>
          <div className="activities-list">
            {activities.length > 0 ? (
              activities.map((activity) => (
                <div key={activity.id} className="activity-card activity-card-featured">
                  <div className="activity-card-header">
                    <h3>{activity.title}</h3>
                    <span className="activity-points-badge">
                      يساوي {activity.points} نقطة
                    </span>
                  </div>
                  <p className="activity-description">{activity.description}</p>
                </div>
              ))
            ) : (
              <p className="no-data">لا توجد أنشطة حالية</p>
            )}
          </div>
        </section>

        {/* قسم المكافآت — مؤقتاً يعرض رصيد النقاط فقط، يُعاد تفعيله عند اكتمال صفحة الكوبونات */}
        <Link to="/profile" className="rewards-points-summary">
          <h2>المكافآت</h2>
          <div className="points-highlight">
            <span className="points-number">{memberData?.points || 0}</span>
            <span className="points-label">نقطة</span>
          </div>
        </Link>

        <section className="dashboard-section">
          <div className="section-header">
            <h2>آخر الإشعارات</h2>
            <Link to="/notifications" className="view-all-link">
              عرض الكل
            </Link>
          </div>
          <div className="notifications-list">
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-card ${
                    notification.isRead ? "read" : "unread"
                  }`}
                  onClick={() => setSelectedNotification(notification)}
                >
                  <div className="notification-header">
                    <h3>{notification.title}</h3>
                    <span className={`notification-type ${notification.type}`}>
                      {notification.type}
                    </span>
                  </div>
                  <p>{notification.content}</p>
                  <div className="notification-footer">
                    <span>
                      {notification.createdAt?.toLocaleString?.() || ""}
                    </span>
                    {!notification.isRead && (
                      <span className="unread-badge">جديد</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="no-data">لا توجد إشعارات</p>
            )}
          </div>
        </section>
      </div>

      {selectedNotification && (
        <NotificationModal
          notification={selectedNotification}
          onConfirm={handleReadNotification}
          onClose={() => setSelectedNotification(null)}
        />
      )}
    </div>
  );
}

export default MemberDashboardPage;
